import { component } from 'picoapp';
import Swiper from 'swiper';
import { repaint } from '../../js/utils/node';
import { hide as hideOverlay, show as showOverlay } from '../../js/utils/overlay';

export default component(async (node, ctx) => {
  /**
   * Data + elements
   */
  const topbar = document.querySelector('.js-topbar');
  const topbarCloseBtn = topbar?.querySelector('.js-topbarCloseBtn');
  const menuBtn = node.querySelector('.js-menuBtn');
  const navSliderElement = node.querySelector('.js-navSlider');
  const megamenus = node.querySelectorAll('.js-megamenu');
  const megamenuTriggers = node.querySelectorAll('.js-megamenuTrigger');

  let previousScrollPos = window.scrollY;

  const headerHeightMobile = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height--mobile'), 10);
  const headerHeightDesktop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height--desktop'), 10);
  let topbarHeight = 0;

  /**
   * Functions
   */
  const initTopbar = () => {
    const closed = sessionStorage.getItem('topbar');

    // Update topbar height for use in handleScroll
    const updateHeight = () => {
      topbarHeight = topbar.getBoundingClientRect().height;
    };

    const close = () => {
      topbar.remove();
      window.dispatchEvent(new Event('resize'));

      if (!closed) {
        sessionStorage.setItem('topbar', 'closed');
      }
    };

    // Remove if already hidden
    if (closed) {
      close();
    }

    updateHeight();
    ctx.on('window:resize', updateHeight);
    topbarCloseBtn.addEventListener('click', close);

    const config = JSON.parse(topbar.dataset.config);
    const { productTitleMarkup } = config;

    if (productTitleMarkup) {
      // Create blueprint for title element
      const blueprint = document.createElement('span');
      blueprint.classList.add('whitespace-nowrap', 'js-productTitle');
      blueprint.innerHTML = productTitleMarkup;

      topbar.appendChild(blueprint);
      const blueprintWidth = Math.ceil(blueprint.getBoundingClientRect().width);
      topbar.removeChild(blueprint);

      const updateTitles = () => {
        // Clear existing titles
        const existingTitles = topbar.querySelectorAll('.js-productTitle');
        existingTitles.forEach((title) => {
          title.remove();
        });

        // Calculate number of titles to add
        const topbarWidth = topbar.getBoundingClientRect().width;
        const topbarPadding = parseInt(getComputedStyle(topbar).getPropertyValue('padding'), 10) || 0 * 2;
        const topbarFreeSpace = topbarWidth - topbarPadding;
        const textElementWidth = Math.ceil(topbar.querySelector('.js-text')?.getBoundingClientRect().width) || 0;
        const freeSpace = Math.ceil(topbarFreeSpace - textElementWidth);
        const numberOfTitlePairs = Math.floor(freeSpace / ((blueprintWidth + 20) * 2));

        // Add title elements
        for (let i = 0; i < numberOfTitlePairs; i += 1) {
          const leftClone = blueprint.cloneNode(true);
          const rightClone = blueprint.cloneNode(true);

          leftClone.style.marginRight = '20px';
          rightClone.style.marginLeft = '20px';

          topbar.insertAdjacentElement('afterbegin', leftClone);
          topbar.insertAdjacentElement('beforeend', rightClone);
        }
      };

      updateTitles();
      ctx.on('window:resize', updateTitles);
    }
  };

  const initMenuBtn = () => {
    menuBtn.addEventListener('click', () => ctx.emit('menu:open'));
  };

  const initNavSlider = () => {
    const currentHref = window.location.href;
    const slides = Array.from(navSliderElement.querySelectorAll('.swiper-slide'));
    const currentSlide = slides.find((slide) => slide.querySelector('a')?.href === currentHref);
    const initialIndex = currentSlide ? slides.indexOf(currentSlide) : 0;

    (() => new Swiper(navSliderElement, {
      slidesPerView: 'auto',
      initialSlide: initialIndex,
    }))();
  };

  const initMegamenus = () => {
    const showMegamenu = (trigger, megamenu) => {
      node.classList.add('header--megamenu-open');
      megamenu.hidden = false;
      repaint(megamenu);
      megamenu.classList.add('header__megamenu--active');

      const listItem = trigger.closest('.js-listItem');
      listItem.classList.add('header__nav__list-item--active');
      listItem.addEventListener('mouseleave', (e) => {
        hideMegamenu(trigger, megamenu);
        hideOverlay();
        const itemLink = listItem.querySelector('a.header__nav__list-item__link');
        itemLink.classList.add('cannot-be-opened');
        setTimeout(() => {
          itemLink.classList.remove('cannot-be-opened');
        }, 500);
        node.classList.remove('header--megamenu-open');
      });
      if (megamenu.classList.contains('header__megamenu--active')) {
        showOverlay();
      }
    };

    const hideMegamenu = (trigger, megamenu) => {
      megamenu.classList.remove('header__megamenu--active');

      const transitionDuration = parseInt(getComputedStyle(megamenu).getPropertyValue('transition-duration') || 0, 10);

      if (transitionDuration === 0) {
        megamenu.hidden = true;
      } else {
        megamenu.addEventListener('transitionend', () => {
          if (!megamenu.classList.contains('header__megamenu--active')) {
            megamenu.hidden = true;
          }
        }, { once: true });
      }

      const listItem = trigger.closest('.js-listItem');
      listItem.classList.remove('header__nav__list-item--active');
    };
    let mouseoverTimeout;

    megamenuTriggers.forEach((trigger) => {
      trigger.addEventListener('mouseover', (event) => {
        event.preventDefault();
        clearTimeout(mouseoverTimeout);
        mouseoverTimeout = setTimeout(() => {
          megamenuTriggers.forEach((otherTrigger) => {
            const megamenu = otherTrigger.parentElement.querySelector('.js-megamenu');
            if (otherTrigger === trigger) {
              if(otherTrigger.classList.contains('cannot-be-opened')) return;
              showMegamenu(otherTrigger, megamenu);
            } else {
              hideMegamenu(otherTrigger, megamenu);
            }
          });
        }, (500)); 
      });
      trigger.addEventListener('mouseleave', () => {
        clearTimeout(mouseoverTimeout);
      });
    });

    window.addEventListener('click', (event) => {
      if (event.target.tagName === 'BUTTON' &&  !event.target.classList.contains('product__add-to-cart-button') && !event.target.classList.contains('js-fitSizeSelectionBtn') ) {
        event.preventDefault();
        return;
      }
      let closeAll = true;

      if (Array.from(megamenus).find((megamenu) => event.target === megamenu || megamenu.contains(event.target))) { // eslint-disable-line
        closeAll = false;
      }

      if (Array.from(megamenuTriggers).find((trigger) => trigger === event.target || trigger.contains(event.target))) { // eslint-disable-line
        closeAll = false;
      }
      if (event.target?.matches('a.js-cart-btn') || (event.target?.closest('a')?.matches('a.js-cart-btn'))) closeAll = false;

      if (closeAll) {
        megamenuTriggers.forEach((trigger) => {
          const megamenu = trigger.parentElement.querySelector('.js-megamenu');
          hideMegamenu(trigger, megamenu);
          if (event.target.closest('#shopify-section-announcement') || event.target.closest('header.header') && !event.target.closest('header.header button[aria-label="Menu"]'))   {
            hideOverlay();
          }
        });

        node.classList.remove('header--megamenu-open');
      }
    });
  };

  const handleScroll = () => {
    let scrollingUp = false;
    const currentScrollPos = window.scrollY;

    if (currentScrollPos < previousScrollPos) {
      scrollingUp = true;
    }

    previousScrollPos = currentScrollPos;

    // Mobile
    if (window.scrollY > (headerHeightMobile + topbarHeight)) {
      node.classList.add('header--fixed-mobile');
      document.body.classList.add('header-fixed--mobile');
    } else {
      node.classList.remove('header--fixed-mobile');
      node.classList.remove('header--transition-transform');
      document.body.classList.remove('header-fixed--mobile');
    }

    if (window.scrollY > (headerHeightMobile + topbarHeight) && scrollingUp) {
      node.classList.add('header--show-mobile');
      node.classList.add('header--transition-transform');
      ctx.emit('mobileHeader:scrolled', ({ mobileHeaderVisible: true }));
    } else {
      node.classList.remove('header--show-mobile');
      ctx.emit('mobileHeader:scrolled', ({ mobileHeaderVisible: false }));
    }

    // Desktop
    if (window.scrollY > (headerHeightDesktop + topbarHeight)) {
      node.classList.add('header--fixed-desktop');
      document.body.classList.add('header-fixed--desktop');
    } else {
      node.classList.remove('header--fixed-desktop');
      document.body.classList.remove('header-fixed--desktop');
    }
  };

  const init = () => {
    if (topbar) {
      initTopbar();
    }

    initMenuBtn();
    initNavSlider();
    initMegamenus();
    handleScroll();

    ctx.on('window:scroll', handleScroll);
  };

  /**
   * Init
   */
  init();
});
